/**
 * Vessel list scoping for Technical/PMS UI with full Custom Role and Assigned Vessel support.
 *
 * Supports:
 * - Super Admin (system role 1 or custom with SUPER_ADMIN behavior) -> sees all active.
 * - Backend Accessible Vessels (from /api/me/accessible-vessels / useAccessibleVessels) -> strictly enforced.
 * - Vessel Access Mode ('ALL', 'ASSIGNED', 'NONE').
 * - Custom Roles matching 'COMPANY_GROUP_ADMIN', 'COMPANY_ADMIN', 'OPERATOR', 'CREW'.
 * - Crew / Shipboard users with assigned vessel.
 */
export function getAccessibleVessels<T extends { id: number; active?: boolean }>(
  vessels: T[],
  currentUser: any,
  isSuperAdmin: boolean,
  roleDetails?: any,
  accessibleVessels?: any[] | null
): T[] {
  const active = (vessels || []).filter((v) => (v as any)?.active ?? true)
  if (isSuperAdmin) return active

  // 1. If backend accessible vessels list is provided, use it as authoritative boundary
  if (Array.isArray(accessibleVessels) && accessibleVessels.length > 0) {
    const allowedIdSet = new Set(accessibleVessels.map((v: any) => Number(v.id)))
    return active.filter((v: any) => allowedIdSet.has(Number(v.id)))
  }

  // 2. Check access mode restrictions on the user
  const accessMode = currentUser?.vesselAccessMode ?? currentUser?.vessel_access_mode
  if (accessMode === 'NONE') {
    return []
  }

  const assignedIds =
    currentUser?.assignedVesselIds ??
    currentUser?.vesselIds ??
    currentUser?.tenant?.vesselIds
  if (accessMode === 'ASSIGNED' && Array.isArray(assignedIds)) {
    if (assignedIds.length === 0) return []
    const allowedIdSet = new Set(assignedIds.map(Number))
    return active.filter((v: any) => allowedIdSet.has(Number(v.id)))
  }

  // 3. Resolve role behavior type (supports both system and custom roles)
  const roleId = Number(currentUser?.roleId ?? currentUser?.role?.id ?? 0)
  const effectiveBehavior = (
    roleDetails?.effectiveBehaviorType ||
    currentUser?.role?.baseRoleType ||
    currentUser?.role?.roleName ||
    currentUser?.role?.roleType ||
    ''
  ).toUpperCase()

  const isCrew = roleId === 4 || effectiveBehavior === 'CREW'
  const vId = currentUser?.vessel?.id

  if (isCrew && vId) {
    return active.filter((v) => Number(v.id) === Number(vId))
  }

  // 4. Resolve company group / subcompany scope
  const cgaId =
    currentUser?.companyGroupAdminId ??
    currentUser?.companyGroupAdmin?.id ??
    (currentUser as any)?.cgaid
  const caId =
    currentUser?.companyAdminId ??
    currentUser?.companyAdmin?.id ??
    (currentUser as any)?.caid

  const actsLikeGroupAdmin =
    roleId === 5 ||
    effectiveBehavior === 'COMPANY_GROUP_ADMIN' ||
    (roleId === 6 && !!cgaId) ||
    (effectiveBehavior === 'OPERATOR' && !!cgaId)

  if (actsLikeGroupAdmin && cgaId) {
    const numericCgaId = Number(cgaId)
    return active.filter(
      (v: any) =>
        Number(v?.companyGroupAdmin?.id ?? v?.companyGroupId ?? v?.cgaid?.id ?? v?.cgaid) ===
        numericCgaId
    )
  }

  const actsLikeCompanyAdmin =
    roleId === 2 ||
    effectiveBehavior === 'COMPANY_ADMIN' ||
    (roleId === 6 && !!caId) ||
    (effectiveBehavior === 'OPERATOR' && !!caId)

  if (actsLikeCompanyAdmin && caId) {
    const numericCaId = Number(caId)
    return active.filter(
      (v: any) =>
        Number(v?.companyAdmin?.id ?? v?.companyId ?? v?.caid?.id ?? v?.caid) ===
        numericCaId
    )
  }

  // If assigned vessel without company scope
  if (vId) {
    return active.filter((v) => Number(v.id) === Number(vId))
  }

  // Fallback: If cgaId or caId exists on user even without explicit role match
  if (cgaId) {
    const numericCgaId = Number(cgaId)
    return active.filter(
      (v: any) =>
        Number(v?.companyGroupAdmin?.id ?? v?.companyGroupId ?? v?.cgaid?.id ?? v?.cgaid) ===
        numericCgaId
    )
  }
  if (caId) {
    const numericCaId = Number(caId)
    return active.filter(
      (v: any) =>
        Number(v?.companyAdmin?.id ?? v?.companyId ?? v?.caid?.id ?? v?.caid) ===
        numericCaId
    )
  }

  // Global operator without company tie: see all active
  return active
}

/** User is assigned to a single vessel and has no company/group admin scope (e.g. typical crew). */
export function isVesselAssignedUserOnly(
  currentUser: any,
  isSuperAdmin: boolean,
  roleDetails?: any
): boolean {
  if (isSuperAdmin) return false
  const effectiveBehavior = (
    roleDetails?.effectiveBehaviorType ||
    currentUser?.role?.baseRoleType ||
    currentUser?.role?.roleName ||
    ''
  ).toUpperCase()

  if (
    effectiveBehavior === 'SUPER_ADMIN' ||
    effectiveBehavior === 'COMPANY_GROUP_ADMIN' ||
    effectiveBehavior === 'COMPANY_ADMIN'
  ) {
    return false
  }

  const cgaId =
    currentUser?.companyGroupAdminId ??
    currentUser?.companyGroupAdmin?.id ??
    currentUser?.cgaid
  const caId =
    currentUser?.companyAdminId ??
    currentUser?.companyAdmin?.id ??
    currentUser?.caid

  if (cgaId || caId) return false
  return Boolean(currentUser?.vessel?.id)
}
